import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { formatMoney } from "@/lib/prefs";
import {
  Plus, Pencil, Trash2, Loader2, Users, Mail, Phone, Calendar, ChevronRight, Upload,
} from "lucide-react";
import { toast } from "sonner";
import BulkStaffImportDialog from "@/components/BulkStaffImportDialog";
import EmptyState from "@/components/EmptyState";

const empty = { name: "", role: "Line Cook", hourly_rate: 25, contact: "", email: "", phone: "", active: true };

function StaffForm({ open, onOpenChange, initial, onSaved, dialCode, setDialCode }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) setForm(initial ? { ...empty, ...initial } : empty);
  }, [open, initial]);

  const onDialChange = (raw) => {
    // Force the input to start with a single leading '+', then digits only.
    let v = (raw || "").trim();
    if (!v.startsWith("+")) v = "+" + v.replace(/[^0-9]/g, "");
    else v = "+" + v.slice(1).replace(/[^0-9]/g, "");
    if (v === "+") v = "";
    setDialCode(v);
    try { localStorage.setItem("cs_dial_code", v); } catch (e) {}
  };

  const submit = async () => {
    setSaving(true);
    try {
      // If the operator typed an area code and the phone does not already
      // start with '+', prepend the dial code so the server persists a
      // clean E.164 number. Leading zeros on the local portion are
      // stripped the same way normalise_phone does on the server.
      let phone = (form.phone || "").trim();
      if (phone && dialCode && !phone.startsWith("+")) {
        const local = phone.replace(/[^0-9]/g, "").replace(/^0+/, "");
        phone = `${dialCode}${local}`;
      }
      const payload = {
        ...form,
        hourly_rate: Number(form.hourly_rate),
        email: (form.email || "").trim() || null,
        phone: phone || null,
      };
      if (isEdit) await api.patch(`/staff/${initial.id}`, payload);
      else await api.post("/staff", payload);
      toast.success(isEdit ? "Staff updated" : "Staff added");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="staff-form">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Edit staff" : "New staff"}</DialogTitle>
          <DialogDescription className="text-white/60 text-sm">
            Email and phone are used for shift publication alerts. Type your country area code once (like +61) and we remember it for every future staff member.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" />
          </div>
          <div>
            <Label>Role</Label>
            <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-role" />
          </div>
          <div>
            <Label>Hourly rate</Label>
            <Input type="number" step="0.5" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="staff-rate" />
          </div>
          <div className="col-span-2">
            <Label>Email</Label>
            <Input
              type="email"
              placeholder="cook@venue.com"
              value={form.email || ""}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="staff-email"
            />
          </div>
          <div className="col-span-2">
            <Label>Phone {dialCode ? <span className="text-white/50">({dialCode} auto prefixed)</span> : <span className="text-white/50">(type the area code once, we remember it)</span>}</Label>
            <div className="flex gap-2">
              <Input
                type="text"
                inputMode="tel"
                value={dialCode || ""}
                onChange={(e) => onDialChange(e.target.value)}
                placeholder="+61"
                className="w-24 tabular text-center"
                aria-label="Area code"
                data-testid="staff-dial-code"
              />
              <Input
                type="tel"
                placeholder="0422 111 222"
                value={form.phone || ""}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="staff-phone"
              />
            </div>
            {form.phone_e164 && (
              <div className="text-[11px] text-emerald-300/80 mt-1 tabular">
                Sending SMS to: {form.phone_e164}
              </div>
            )}
          </div>
          <div className="col-span-2">
            <Label>Notes (optional)</Label>
            <Input
              value={form.contact || ""}
              onChange={(e) => setForm({ ...form, contact: e.target.value })}
              placeholder="Emergency contact, notes..."
              data-testid="staff-contact"
            />
          </div>
          <div className="col-span-2 flex items-center justify-between border border-border rounded p-3">
            <div>
              <div className="text-sm font-medium">Active</div>
              <div className="text-xs text-muted-foreground">Available for rostering</div>
            </div>
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="staff-active" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.name} data-testid="staff-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UpcomingShiftsDrawer({ staff, open, onOpenChange }) {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !staff?.id) return;
    setLoading(true);
    api.get(`/staff/${staff.id}/upcoming-shifts`, { params: { days: 14 } })
      .then((r) => setShifts(r.data?.shifts || []))
      .catch(() => toast.error("Could not load upcoming shifts."))
      .finally(() => setLoading(false));
  }, [open, staff?.id]);

  const grouped = useMemo(() => {
    const map = {};
    for (const s of shifts) (map[s.date] ||= []).push(s);
    return map;
  }, [shifts]);

  if (!staff) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-white/10 max-w-lg" data-testid="staff-upcoming-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Calendar className="w-5 h-5 text-amber-200" />
            {staff.name}: upcoming shifts
          </DialogTitle>
          <DialogDescription className="text-white/60 text-sm">
            The next 14 days from the roster. Every shift on this list has already been published to {staff.name}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1" data-testid="staff-upcoming-list">
          {loading && (
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading roster
            </div>
          )}
          {!loading && shifts.length === 0 && (
            <div className="text-sm text-white/60">
              No shifts scheduled in the next 14 days.
            </div>
          )}
          {Object.entries(grouped).map(([date, dayShifts]) => (
            <div key={date} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
              <div className="label-eyebrow mb-2">{date}</div>
              <div className="space-y-1.5">
                {dayShifts.map((s) => (
                  <div key={s.id} className="flex items-center justify-between text-sm">
                    <span className="tabular text-white/90">{s.start_time} to {s.end_time}</span>
                    <span className="text-white/50 text-xs">{s.notes || staff.role}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Staff() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dialCode, setDialCode] = useState("");
  const [drawerStaff, setDrawerStaff] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        api.get("/staff"),
        api.get("/staff/dial-code").catch(() => ({ data: {} })),
      ]);
      setItems(s.data);
      // Prefer the user's manually-typed override (localStorage) so a
      // typed area code sticks across reloads. Fall back to what the
      // server thinks the region is.
      let stored = "";
      try { stored = localStorage.getItem("cs_dial_code") || ""; } catch (e) {}
      setDialCode(stored || d.data?.dial_code || "");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this staff member? Any of their future shifts will also be removed. Past shifts stay intact for cost history.")) return;
    try {
      const { data } = await api.delete(`/staff/${id}`);
      const cascade = data?.shifts_removed || 0;
      toast.success(cascade
        ? `Removed. ${cascade} future shift${cascade === 1 ? "" : "s"} also cleared.`
        : "Removed."
      );
      load();
    } catch {
      toast.error("Could not remove staff member.");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-6xl mx-auto space-y-6" data-testid="staff-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Users className="w-3 h-3" /> Team</div>
          <h1 className="font-display text-3xl md:text-4xl leading-none mt-2">Staff</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-md">
            Roles, hourly rates, and contact channels. When you publish a shift, the platform emails and texts the assigned staff member automatically {dialCode ? <span>using the {dialCode} dial code.</span> : "."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setBulkOpen(true)} data-testid="staff-bulk-btn">
            <Upload className="w-4 h-4" /> Bulk import
          </Button>
          <Button className="gap-2" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="staff-new-btn">
            <Plus className="w-4 h-4" /> New staff
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <EmptyState
          illustration="brigade"
          eyebrow="Empty brigade"
          title="Bring your team on."
          body="Add your chefs, servers, and prep cooks so you can roster shifts, track hourly labor cost, and text a shift alert the moment a schedule goes live."
          ctaLabel="Add your first staff member"
          ctaIcon={Plus}
          onCtaClick={() => { setEditing(null); setDialogOpen(true); }}
          ctaTestId="empty-staff-add"
          secondaryLabel="Bulk import a CSV"
          onSecondaryClick={() => setBulkOpen(true)}
        />
      ) : (
        <div className="rounded-2xl glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="text-left border-b border-border">
                  <th className="px-4 py-3 label-eyebrow font-bold">Name</th>
                  <th className="px-4 py-3 label-eyebrow font-bold">Role</th>
                  <th className="px-4 py-3 label-eyebrow font-bold">Email</th>
                  <th className="px-4 py-3 label-eyebrow font-bold">Phone</th>
                  <th className="px-4 py-3 label-eyebrow font-bold text-right">Rate / hr</th>
                  <th className="px-4 py-3 label-eyebrow font-bold">Status</th>
                  <th className="px-4 py-3 label-eyebrow font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="border-b border-border/60 hover:bg-secondary/40" data-testid={`staff-row-${s.id}`}>
                    <td className="px-4 py-3 font-medium">
                      <button
                        type="button"
                        className="hover:text-amber-100 inline-flex items-center gap-1"
                        onClick={() => setDrawerStaff(s)}
                        data-testid={`staff-open-${s.id}`}
                      >
                        {s.name}
                        <ChevronRight className="w-3.5 h-3.5 opacity-60" />
                      </button>
                      {s.contact && <div className="text-xs text-muted-foreground">{s.contact}</div>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{s.role}</td>
                    <td className="px-4 py-3">
                      {s.email ? (
                        <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1 text-white/85 hover:text-amber-100">
                          <Mail className="w-3.5 h-3.5" /> {s.email}
                        </a>
                      ) : (
                        <span className="text-white/40 text-xs">not set</span>
                      )}
                    </td>
                    <td className="px-4 py-3 tabular">
                      {s.phone_e164 || s.phone ? (
                        <a href={`tel:${s.phone_e164 || s.phone}`} className="inline-flex items-center gap-1 text-white/85 hover:text-amber-100">
                          <Phone className="w-3.5 h-3.5" /> {s.phone_e164 || s.phone}
                        </a>
                      ) : (
                        <span className="text-white/40 text-xs">not set</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular">{formatMoney(s.hourly_rate)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${s.active ? "bg-primary/15 gold" : "bg-secondary text-muted-foreground"}`}>
                        {s.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => setDrawerStaff(s)}
                          data-testid={`staff-shifts-${s.id}`}
                          title="View upcoming shifts"
                        >
                          <Calendar className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setEditing(s); setDialogOpen(true); }} data-testid={`staff-edit-${s.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(s.id)} data-testid={`staff-delete-${s.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!items.length && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No staff yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <StaffForm open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSaved={load} dialCode={dialCode} setDialCode={setDialCode} />
      <UpcomingShiftsDrawer staff={drawerStaff} open={!!drawerStaff} onOpenChange={(v) => !v && setDrawerStaff(null)} />
      <BulkStaffImportDialog open={bulkOpen} onOpenChange={setBulkOpen} onImported={load} />
    </div>
  );
}
